/**
 * Accept Cabinet Invitation Page
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 *
 * Page permettant a un client d'accepter une invitation de cabinet comptable
 */

import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '@/components/ui-v2';
import { useAcceptInvitation } from '../hooks/useCabinet';

export const AcceptInvitationPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'pending' | 'accepting' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const acceptMutation = useAcceptInvitation();

  const handleAccept = async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('cabinet.invitation.missingToken'));
      return;
    }

    setStatus('accepting');

    try {
      await acceptMutation.mutateAsync(token);
      setStatus('success');
    } catch (error: any) {
      setStatus('error');
      setErrorMessage(error.message || t('cabinet.invitation.acceptError'));
    }
  };

  const cardClasses = `bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl
    border border-white/40 p-8 max-w-md w-full mx-4`;

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className={cardClasses}>
          <div className="text-center">
            <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{t('cabinet.invitation.invalidLink')}</h1>
            <p className="text-gray-500 mb-6">
              {t('cabinet.invitation.invalidLinkDescription')}
            </p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              {t('cabinet.invitation.backToDashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className={cardClasses}>
        {status === 'pending' && (
          <div className="text-center">
            <div className="p-4 bg-teal-100 rounded-2xl w-fit mx-auto mb-4">
              <Building2 className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('cabinet.invitation.title')}
            </h1>
            <p className="text-gray-500 mb-6">
              {t('cabinet.invitation.description')}
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium mb-1">{t('cabinet.invitation.warningTitle')}</p>
                  <p>
                    {t('cabinet.invitation.warningText')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="flex-1">
                {t('cabinet.invitation.decline')}
              </Button>
              <Button variant="primary" onClick={handleAccept} className="flex-1">
                {t('cabinet.invitation.accept')}
              </Button>
            </div>
          </div>
        )}

        {status === 'accepting' && (
          <div className="text-center py-8">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-500">{t('cabinet.invitation.processing')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="p-4 bg-green-100 rounded-2xl w-fit mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('cabinet.invitation.accepted')}
            </h1>
            <p className="text-gray-500 mb-6">
              {t('cabinet.invitation.acceptedDescription')}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/dashboard')}
              icon={<ArrowRight className="w-4 h-4" />}
            >
              {t('cabinet.invitation.goToDashboard')}
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('cabinet.invitation.error')}
            </h1>
            <p className="text-gray-500 mb-6">{errorMessage}</p>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="flex-1">
                {t('cabinet.invitation.backToDashboard')}
              </Button>
              <Button variant="primary" onClick={() => setStatus('pending')} className="flex-1">
                {t('cabinet.invitation.retry')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
